// Clients list page — primary CRM working surface + Phase 9.7.A
// self-registered backlog view.
//
// Two top-level modes (controlled via ?self_registered URL state so a deep
// link to the backlog can be shared with a rep):
//   • "All clients"          — default. Existing search + status filter +
//                              status / credit-hold / last-updated columns.
//   • "Self-registered"      — pending-review queue. Different columns
//                              (submitter + submitted-at + actions) + per-
//                              row Approve / Merge / Reject CTAs.
//
// Action dialogs are inline (controlled state on the page, rendered as
// overlay portals). Keeping them inline avoids a new modal primitive
// and keeps state co-located with the table.
import { useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, Merge, Plus, Search, Sparkles, X } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { FlashBanner } from "@/components/common/FlashBanner";
import { PageHeader } from "@/components/ui/PageHeader";
import {
  FilterTabs,
  MetricStrip,
  TABLE_HEAD,
  TABLE_WRAP,
  TD,
  TH,
  TROW,
} from "@/components/ui/RecordParts";
import {
  StatusPill,
  clientStatusTone,
} from "@/components/ui/StatusPill";
import { api, ApiError } from "@/lib/api";
import { useAuth } from "@/auth/AuthProvider";
import { useIndustryOptions } from "@/lib/useIndustryOptions";
import { timeAgo } from "@/lib/utils";
import { LeadSourceField, leadSourceBody, leadSourceComplete, useLeadSourceReady, type LeadSourceValue } from "@/components/clients/LeadSourceField";

interface ClientRow {
  id: string;
  name: string;
  status: string | null;
  clientRepId: string | null;
  onCreditHold: boolean | null;
  notes: string | null;
  industry: string | null;
  selfRegistered: boolean | null;
  selfRegisteredBy: string | null;
  selfRegisteredByName: string | null;
  selfRegisteredByEmail: string | null;
  reviewedAt: string | null;
  reviewedBy: string | null;
  createdAt: string | null;
  updatedAt: string | null;
}

interface ClientsListResponse {
  rows: ClientRow[];
  nextCursor: { createdAt: string; id: string } | null;
}

const STATUS_FILTERS = [
  { value: undefined, label: "All" },
  { value: "active", label: "Active" },
  { value: "prospect", label: "Prospect" },
  { value: "dormant", label: "Dormant" },
  { value: "closed", label: "Closed" },
] as const;

export function ClientsListPage() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const backlogView = searchParams.get("self_registered") === "true";
  const industries = useIndustryOptions();
  // Everyone starts on their OWN accounts, with "All accounts" one click away
  // (Stephanie 2026-10-16). ?who=all keeps the choice in the address.
  // Stephanie 2026-11-02: "why not show recent accounts that have been
  // accessed" — Recent (your last 25 opened, on every device) is the default
  // once the DB step has run; until then the page starts on My accounts as before.
  const recentQuery = useQuery({
    queryKey: ["clients", "recent"],
    queryFn: () => api<{ ready: boolean; rows: Array<ClientsListResponse["rows"][number] & { viewedAt: string }> }>("/clients/recent"),
    enabled: !!user?.sub,
  });
  const recentReady = recentQuery.data?.ready === true;
  const who = searchParams.get("who");
  const view: "recent" | "mine" | "all" =
    who === "all" ? "all" : who === "mine" || !recentReady ? "mine" : "recent";
  const showAll = view === "all";
  const mineOnly = view === "mine" && !!user?.sub;
  const setView = (v: "recent" | "mine" | "all") => {
    const next = new URLSearchParams(searchParams);
    if (v === "recent") next.delete("who");
    else if (v === "mine" && !recentReady) next.delete("who");
    else next.set("who", v);
    setSearchParams(next);
  };
  const setShowAll = (all: boolean) => setView(all ? "all" : "mine");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<string | undefined>(undefined);
  // Recent shows your opened accounts; searching or picking a status looks
  // through every account.
  const recentList = view === "recent" && !search.trim() && !statusFilter && !backlogView;
  const [seeding, setSeeding] = useState(false);
  const [seedError, setSeedError] = useState<string | null>(null);

  const isAdmin = user?.roles.includes("admin") ?? false;
  const canCreateClient =
    (user?.roles.includes("admin") || user?.roles.includes("client_rep")) ??
    false;
  const [isCreatingClient, setIsCreatingClient] = useState(false);

  // Count of pending self-registered clients drives the "Backlog (N)" tab
  // label — fetched separately so it's visible even in the All view.
  const backlogCountQuery = useQuery({
    queryKey: ["clients", "backlog-count"],
    queryFn: () =>
      api<ClientsListResponse>(
        "/clients?self_registered=true&reviewed=false&limit=100&sort=newest",
      ),
    refetchOnWindowFocus: true,
  });
  const backlogCount = backlogCountQuery.data?.rows.length ?? 0;

  const queryString = useMemo(() => {
    const params = new URLSearchParams();
    if (backlogView) {
      params.set("self_registered", "true");
      params.set("reviewed", "false");
      params.set("limit", "100");
      params.set("sort", "newest"); // the review queue stays newest first
      return params.toString();
    }
    if (search.trim()) params.set("search", search.trim());
    if (statusFilter) params.set("status", statusFilter);
    if (mineOnly) params.set("client_rep_id", user!.sub);
    params.set("limit", "100");
    return params.toString();
  }, [backlogView, search, statusFilter, mineOnly, user]);

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ["clients", "list", queryString],
    queryFn: () => api<ClientsListResponse>(`/clients?${queryString}`),
    enabled: !recentList,
  });

  const rows = recentList ? (recentQuery.data?.rows ?? []) : (data?.rows ?? []);
  const isEmpty =
    !isLoading && rows.length === 0 && !search && !statusFilter && !backlogView && !mineOnly;

  const setBacklog = (next: boolean) => {
    if (next) {
      setSearchParams({ self_registered: "true" });
    } else {
      setSearchParams({});
    }
  };

  const handleSeed = async () => {
    setSeeding(true);
    setSeedError(null);
    try {
      await api<{ created: { clients: number } }>(
        "/admin/test-data/seed?count=30",
        { method: "POST" },
      );
      await queryClient.invalidateQueries({ queryKey: ["clients"] });
      await refetch();
    } catch (err) {
      const msg =
        err instanceof ApiError
          ? `Seed failed (${err.status})`
          : "Seed failed — see console";
      setSeedError(msg);
      console.error("[seed] failed", err);
    } finally {
      setSeeding(false);
    }
  };

  // Metric strip (handoff W03): counts over the latest 100 accounts regardless
  // of the filters below, so the numbers don't jump while the rep searches.
  // The counts follow My / All accounts.
  const summaryQuery = useQuery({
    queryKey: ["clients", "list", "summary", !showAll && user?.sub ? user.sub : "all"],
    queryFn: () =>
      api<ClientsListResponse>(
        !showAll && user?.sub ? `/clients?limit=100&client_rep_id=${user.sub}` : "/clients?limit=100",
      ),
  });
  const summaryRows = summaryQuery.data?.rows ?? null;
  const countStatus = (s: string): number | null =>
    summaryRows === null ? null : summaryRows.filter((r) => r.status === s).length;
  const holdCount =
    summaryRows === null ? null : summaryRows.filter((r) => r.onCreditHold).length;

  return (
    <div className="mx-auto w-full">
      <FlashBanner />
      <PageHeader
        eyebrow={
          <>
            Sales / <span>Accounts</span>
          </>
        }
        title={backlogView ? "Self-registered backlog" : "Accounts"}
        intro={
          backlogView
            ? "Brand-new companies that signed up via the client portal. Review each one — approve to claim, merge if they already exist in the CRM, or reject if it's noise."
            : "Every company you sell to. Open one for its contacts, addresses, projects and activity."
        }
        action={
          !backlogView && (isAdmin || canCreateClient) ? (
            <>
              {/* "Seed sample data" removed (pilot 2026-10-13): Flow runs on the
                  live database, where the server refuses to seed anyway. The
                  empty-accounts state still offers it for a fresh database. */}
              {canCreateClient && (
                <Button variant="primary" onClick={() => setIsCreatingClient(true)}>
                  <Plus className="h-4 w-4" />
                  New account
                </Button>
              )}
            </>
          ) : undefined
        }
      />
      {isCreatingClient && (
        <NewClientDialog onClose={() => setIsCreatingClient(false)} />
      )}

      {!backlogView && (
        <div role="radiogroup" aria-label="Whose accounts" className="mb-5 flex w-fit rounded-full bg-canvas p-1">
          {(
            [
              ...(recentReady ? ([["recent", "Recent"]] as const) : []),
              ["mine", "My accounts"],
              ["all", "All accounts"],
            ] as const
          ).map(([v, label]) => (
            <button
              key={label}
              type="button"
              role="radio"
              aria-checked={view === v}
              onClick={() => setView(v)}
              className={`rounded-full px-4 py-1.5 text-sm font-semibold transition-colors ${
                view === v ? "bg-ink text-white" : "text-muted hover:text-ink"
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      )}

      {!backlogView && !summaryQuery.isError && (
        <div className="mb-7">
          <MetricStrip
            ariaLabel="Account summary"
            cells={[
              { label: "Active", value: countStatus("active"), note: "Buying now", onClick: () => setStatusFilter("active") },
              { label: "Prospects", value: countStatus("prospect"), note: "Not buying yet", onClick: () => setStatusFilter("prospect") },
              { label: "Dormant", value: countStatus("dormant"), note: "Worth a check-in", onClick: () => setStatusFilter("dormant") },
              {
                label: "Self-registered to review",
                value: backlogCountQuery.isLoading ? null : backlogCount,
                note: holdCount ? `${holdCount} on credit hold` : "Portal sign-ups",
                attention: backlogCount > 0,
                onClick: () => setBacklog(true),
              },
            ]}
          />
          {summaryQuery.data?.nextCursor && (
            <p className="mt-2 text-xs text-muted">Counts cover the latest 100 accounts.</p>
          )}
        </div>
      )}

      {/* View + status tabs, then search */}
      <div className="mb-4 flex flex-wrap items-end justify-between gap-x-6 gap-y-3 border-b border-rule">
        <div className="-mb-px flex flex-wrap gap-x-5">
          {backlogView ? (
            <>
              <TabButton active={false} onClick={() => setBacklog(false)}>
                All accounts
              </TabButton>
              <TabButton active onClick={() => setBacklog(true)}>
                Self-registered{backlogCount > 0 ? ` (${backlogCount})` : ""}
              </TabButton>
            </>
          ) : (
            <>
              <FilterTabs
                ariaLabel="Filter by status"
                options={STATUS_FILTERS}
                value={statusFilter}
                onChange={(v) => setStatusFilter(v)}
              />
              <TabButton active={false} onClick={() => setBacklog(true)}>
                Self-registered{backlogCount > 0 ? ` (${backlogCount})` : ""}
              </TabButton>
            </>
          )}
        </div>
        {!backlogView && (
          <div className="mb-2 flex w-full flex-wrap gap-2 sm:w-auto">
            {/* Industry filter — shortcut that drives the same search param
                (backend search matches name OR industry OR notes). Picking a
                vertical fills the search box; free-text typing resets it. */}
            <select
              aria-label="Filter by industry"
              value={
                industries.includes(search)
                  ? search
                  : ""
              }
              onChange={(e) => setSearch(e.target.value)}
              className="h-10 min-w-0 flex-1 rounded-md border border-rule bg-white px-3 text-sm text-ink sm:w-44 sm:flex-none"
            >
              <option value="">All industries</option>
              {industries.map((v) => (
                <option key={v} value={v}>
                  {v}
                </option>
              ))}
            </select>
            <div className="relative w-full sm:w-64">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted" aria-hidden />
              <Input
                placeholder="Search name, industry or notes"
                aria-label="Search accounts by name, industry or notes"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
              />
            </div>
          </div>
        )}
      </div>

      {seedError && (
        <div className="mb-4 rounded-md border border-risk/30 bg-risk-bg px-3 py-2 text-sm text-risk" role="alert">
          {seedError}
        </div>
      )}

      {/* Table */}
      <div className={TABLE_WRAP} role="region" aria-label={backlogView ? "Self-registered accounts" : "Accounts"} tabIndex={0}>
        {(recentList ? recentQuery.isLoading : isLoading) ? (
          <div className="space-y-3 p-5" aria-label="Loading accounts">
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="h-4 w-full animate-pulse rounded bg-canvas" />
            ))}
          </div>
        ) : (recentList ? recentQuery.isError : isError) ? (
          <div className="p-5 text-sm text-risk" role="alert">
            We couldn't load accounts. Nothing has changed — refresh to try again.
          </div>
        ) : rows.length === 0 ? (
          recentList ? (
            <div className="px-5 py-12 text-center">
              <p className="font-medium text-ink">No recently opened accounts yet.</p>
              <p className="mt-1 text-sm text-muted">Accounts you open show here, newest first. Search above to find any account.</p>
              <Button variant="outline" className="mt-4" onClick={() => setView("mine")}>
                See my accounts
              </Button>
            </div>
          ) : backlogView ? (
            <BacklogEmpty />
          ) : (
            mineOnly && !search && !statusFilter ? (
              <div className="px-5 py-12 text-center">
                <p className="font-medium text-ink">You don&apos;t have any accounts yet.</p>
                <p className="mt-1 text-sm text-muted">Accounts where you&apos;re the account rep show here.</p>
                <Button variant="outline" className="mt-4" onClick={() => setShowAll(true)}>
                  See all accounts
                </Button>
              </div>
            ) : (
              <EmptyState
                empty={isEmpty}
                isAdmin={isAdmin}
                onSeed={handleSeed}
                seeding={seeding}
              />
            )
          )
        ) : backlogView ? (
          <BacklogTable rows={rows} />
        ) : (
          <AllClientsTable rows={rows} />
        )}
      </div>
      <p className="mt-3 text-xs text-muted">
        {recentList ? (
          <>Your last {rows.length} opened account{rows.length === 1 ? "" : "s"}, newest first. Search to find any other.</>
        ) : (
          <>
            Showing {rows.length} {backlogView ? "pending" : "account"}
            {rows.length === 1 ? "" : "s"}
            {data?.nextCursor && !backlogView && " · more available — narrow the search to find the rest"}
          </>
        )}
      </p>
    </div>
  );
}

// ─── Tab toggle ──────────────────────────────────────────────────────

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={`min-h-[40px] border-b-2 px-0.5 text-sm transition-colors ${
        active
          ? "border-ink font-medium text-ink"
          : "border-transparent text-muted hover:text-ink"
      }`}
    >
      {children}
    </button>
  );
}

// ─── All-clients table (existing UX) ─────────────────────────────────

function AllClientsTable({ rows }: { rows: ClientRow[] }) {
  return (
    <table className="w-full text-left">
      <thead className={TABLE_HEAD}>
        <tr>
          <th scope="col" className={TH}>Account</th>
          <th scope="col" className={TH}>Status</th>
          <th scope="col" className={`${TH} hidden sm:table-cell`}>Credit hold</th>
          <th scope="col" className={`${TH} hidden sm:table-cell`}>Last updated</th>
        </tr>
      </thead>
      <tbody className="text-[13px]">
        {rows.map((c) => (
          <tr key={c.id} className={TROW}>
            <td className={TD}>
              <Link
                to={`/clients/${c.id}`}
                className="font-medium text-ink hover:underline"
              >
                {c.name}
              </Link>
              {(c.industry || c.notes) && (
                <span className="mt-0.5 block max-w-md truncate text-[11px] text-muted">
                  {[c.industry, c.notes].filter(Boolean).join(" · ")}
                </span>
              )}
            </td>
            <td className={TD}>
              <StatusPill tone={clientStatusTone(c.status)}>
                {c.status ?? "unknown"}
              </StatusPill>
              {c.onCreditHold && (
                <span className="ml-1.5 sm:hidden">
                  <StatusPill tone="warn">On hold</StatusPill>
                </span>
              )}
            </td>
            <td className={`${TD} hidden sm:table-cell`}>
              {c.onCreditHold ? (
                <StatusPill tone="warn">On hold</StatusPill>
              ) : (
                <span className="text-muted">—</span>
              )}
            </td>
            <td className={`${TD} hidden text-muted sm:table-cell`}>
              {timeAgo(c.updatedAt)}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// ─── Backlog table (Phase 9.7.A) ─────────────────────────────────────

function BacklogTable({ rows }: { rows: ClientRow[] }) {
  return (
    <table className="w-full text-left">
      <thead className={TABLE_HEAD}>
        <tr>
          <th scope="col" className={TH}>Company</th>
          <th scope="col" className={TH}>Submitted by</th>
          <th scope="col" className={TH}>Submitted</th>
          <th scope="col" className={`${TH} text-right`}>Actions</th>
        </tr>
      </thead>
      <tbody className="text-[13px]">
        {rows.map((c) => (
          <BacklogRow key={c.id} client={c} />
        ))}
      </tbody>
    </table>
  );
}

function BacklogRow({ client: c }: { client: ClientRow }) {
  const [dialog, setDialog] = useState<"approve" | "merge" | "reject" | null>(
    null,
  );
  return (
    <>
      <tr className={TROW}>
        <td className={TD}>
          <Link
            to={`/clients/${c.id}`}
            className="font-medium text-ink hover:underline"
          >
            {c.name}
          </Link>
          {c.notes && (
            <div className="mt-1 line-clamp-1 text-xs text-brown-300">
              {c.notes}
            </div>
          )}
        </td>
        <td className={TD}>
          <div className="font-medium text-brown-500">
            {c.selfRegisteredByName ?? "—"}
          </div>
          {c.selfRegisteredByEmail && (
            <div className="text-xs text-brown-300">
              {c.selfRegisteredByEmail}
            </div>
          )}
        </td>
        <td className={`${TD} text-muted`}>
          {timeAgo(c.createdAt)}
        </td>
        <td className={TD}>
          <div className="flex flex-wrap justify-end gap-2">
            <Button
              variant="primary"
              size="sm"
              onClick={() => setDialog("approve")}
              title="Approve and claim as rep"
            >
              <Check className="h-3.5 w-3.5" />
              Approve
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setDialog("merge")}
              title="Merge into an existing account"
            >
              <Merge className="h-3.5 w-3.5" />
              Merge
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setDialog("reject")}
              title="Reject this submission"
            >
              <X className="h-3.5 w-3.5" />
              Reject
            </Button>
          </div>
        </td>
      </tr>
      {dialog === "approve" && (
        <ApproveDialog client={c} onClose={() => setDialog(null)} />
      )}
      {dialog === "merge" && (
        <MergeDialog client={c} onClose={() => setDialog(null)} />
      )}
      {dialog === "reject" && (
        <RejectDialog client={c} onClose={() => setDialog(null)} />
      )}
    </>
  );
}

// ─── Dialogs ─────────────────────────────────────────────────────────

function DialogShell({
  title,
  onClose,
  children,
}: {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
}) {
  return (
    <tr>
      <td colSpan={4} className="px-0 py-0">
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-brown-500/40 px-4"
          onClick={(e) => {
            if (e.target === e.currentTarget) onClose();
          }}
        >
          <div className="w-full max-w-md rounded-lg border border-brown-100 bg-white shadow-xl">
            <div className="flex items-center justify-between border-b border-brown-50 px-5 py-3">
              <h2 className="font-display text-lg font-bold text-brown-500">
                {title}
              </h2>
              <button
                type="button"
                onClick={onClose}
                className="text-brown-300 hover:text-brown-500"
                aria-label="Close dialog"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="px-5 py-4">{children}</div>
          </div>
        </div>
      </td>
    </tr>
  );
}

function ApproveDialog({
  client,
  onClose,
}: {
  client: ClientRow;
  onClose: () => void;
}) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<"prospect" | "active">("prospect");
  const mutation = useMutation({
    mutationFn: () =>
      api(`/clients/${client.id}/approve`, {
        method: "POST",
        body: JSON.stringify({
          client_rep_id: user?.sub,
          status,
        }),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["clients"] });
      onClose();
    },
  });
  const errorMsg =
    mutation.error instanceof ApiError
      ? mutation.error.status === 409
        ? "Already reviewed by another rep — refresh to see latest state."
        : `Approve failed (${mutation.error.status})`
      : mutation.error
        ? "Approve failed — see console"
        : null;
  return (
    <DialogShell title={`Approve ${client.name}`} onClose={onClose}>
      <p className="text-sm text-brown-400">
        You'll be assigned as the rep. The account moves out of the backlog
        and into your account list.
      </p>
      <div className="mt-4">
        <label className="block text-[11px] font-medium uppercase tracking-[0.12em] text-muted">
          Status
        </label>
        <div className="mt-2 flex gap-2">
          {(["prospect", "active"] as const).map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => setStatus(s)}
              className={`rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-wide transition-colors ${
                status === s
                  ? "bg-brown-500 text-cream-50"
                  : "bg-cream-100 text-brown-400 hover:bg-cream-200"
              }`}
            >
              {s}
            </button>
          ))}
        </div>
      </div>
      {errorMsg && (
        <p className="mt-4 rounded-md border border-rust-300 bg-rust-50 px-3 py-2 text-sm text-rust-600">
          {errorMsg}
        </p>
      )}
      <div className="mt-6 flex justify-end gap-2">
        <Button variant="ghost" size="sm" onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant="primary"
          size="sm"
          onClick={() => mutation.mutate()}
          disabled={mutation.isPending}
        >
          {mutation.isPending ? "Approving…" : "Approve and claim"}
        </Button>
      </div>
    </DialogShell>
  );
}

function MergeDialog({
  client,
  onClose,
}: {
  client: ClientRow;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState("");
  const [selected, setSelected] = useState<ClientRow | null>(null);

  // Merge-target search: no self_registered filter (backend rejects invalid
  // targets — pending-unreviewed → 409 target_is_self_registered, self →
  // 409 target_is_source). Excluding here would hide legitimate "approved
  // self-registered then later this signup arrives" matches.
  const search = useQuery({
    queryKey: ["clients", "merge-search", searchTerm],
    queryFn: () =>
      api<ClientsListResponse>(
        `/clients?search=${encodeURIComponent(searchTerm.trim())}&limit=10`,
      ),
    enabled: searchTerm.trim().length >= 2,
  });

  const mutation = useMutation({
    mutationFn: () => {
      if (!selected) throw new Error("no target selected");
      return api(`/clients/${client.id}/merge`, {
        method: "POST",
        body: JSON.stringify({ target_client_id: selected.id }),
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["clients"] });
      onClose();
    },
  });

  let errorMsg: string | null = null;
  if (mutation.error instanceof ApiError) {
    if (mutation.error.status === 409) {
      const body = (mutation.error.body ?? {}) as {
        error?: string;
        reason?: string;
        detail?: string | null;
      };
      if (body.error === "account_has_sub_accounts") {
        errorMsg =
          body.detail ??
          "This account has active sub-accounts; reassign or remove them first.";
      } else if (body.error === "merge_target_invalid") {
        if (body.reason === "contact_email_conflict") {
          errorMsg =
            "Target already has a contact with the same email — handle this one manually.";
        } else if (body.reason === "target_is_self_registered") {
          errorMsg =
            "Target is itself a pending self-registration — approve the target first.";
        } else if (body.reason === "target_not_found") {
          errorMsg = "Target account not found or deleted.";
        } else {
          errorMsg = body.detail ?? "Merge target is invalid.";
        }
      } else {
        errorMsg = "Already reviewed by another rep — refresh to see latest state.";
      }
    } else {
      errorMsg = `Merge failed (${mutation.error.status})`;
    }
  } else if (mutation.error) {
    errorMsg = "Merge failed — see console";
  }

  return (
    <DialogShell title={`Merge ${client.name} into…`} onClose={onClose}>
      <p className="text-sm text-brown-400">
        Pick an existing account to fold this submission into. The portal user,
        their contact record, brand assets, and project requests will move
        across. The duplicate row is soft-deleted.
      </p>
      <div className="mt-4">
        <label className="block text-[11px] font-medium uppercase tracking-[0.12em] text-muted">
          Search target
        </label>
        <Input
          className="mt-2"
          placeholder="Type at least 2 characters…"
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setSelected(null);
          }}
        />
        {searchTerm.trim().length >= 2 && (
          <div className="mt-2 max-h-48 overflow-y-auto rounded-md border border-brown-100">
            {search.isLoading ? (
              <p className="px-3 py-2 text-xs text-brown-300">Searching…</p>
            ) : (() => {
                // Hide the source client from its own merge picker.
                const candidates =
                  search.data?.rows.filter((r) => r.id !== client.id) ?? [];
                if (candidates.length === 0) {
                  return (
                    <p className="px-3 py-2 text-xs text-brown-300">
                      No matches.
                    </p>
                  );
                }
                return candidates.map((r) => (
                  <button
                    key={r.id}
                    type="button"
                    onClick={() => setSelected(r)}
                    className={`flex w-full items-center justify-between px-3 py-2 text-left text-sm transition-colors ${
                      selected?.id === r.id
                        ? "bg-peach-100 text-brown-500"
                        : "text-brown-400 hover:bg-cream-100"
                    }`}
                  >
                    <span className="font-medium">{r.name}</span>
                    <StatusPill tone={clientStatusTone(r.status)}>
                      {r.status ?? "unknown"}
                    </StatusPill>
                  </button>
                ));
              })()}
          </div>
        )}
      </div>
      {errorMsg && (
        <p className="mt-4 rounded-md border border-rust-300 bg-rust-50 px-3 py-2 text-sm text-rust-600">
          {errorMsg}
        </p>
      )}
      <div className="mt-6 flex justify-end gap-2">
        <Button variant="ghost" size="sm" onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant="primary"
          size="sm"
          onClick={() => mutation.mutate()}
          disabled={!selected || mutation.isPending}
        >
          {mutation.isPending ? "Merging…" : selected ? `Merge into ${selected.name}` : "Pick a target"}
        </Button>
      </div>
    </DialogShell>
  );
}

function RejectDialog({
  client,
  onClose,
}: {
  client: ClientRow;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [reason, setReason] = useState("");
  const mutation = useMutation({
    mutationFn: () =>
      api(`/clients/${client.id}/reject`, {
        method: "POST",
        body: JSON.stringify({ reason: reason.trim() }),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["clients"] });
      onClose();
    },
  });
  let errorMsg: string | null = null;
  if (mutation.error instanceof ApiError) {
    if (mutation.error.status === 409) {
      const body = (mutation.error.body ?? {}) as {
        error?: string;
        detail?: string | null;
      };
      errorMsg =
        body.error === "account_has_sub_accounts"
          ? (body.detail ??
            "This account has active sub-accounts; reassign or remove them first.")
          : "Already reviewed by another rep — refresh to see latest state.";
    } else {
      errorMsg = `Reject failed (${mutation.error.status})`;
    }
  } else if (mutation.error) {
    errorMsg = "Reject failed — see console";
  }
  return (
    <DialogShell title={`Reject ${client.name}`} onClose={onClose}>
      <p className="text-sm text-brown-400">
        The account is soft-deleted and the portal user is deactivated.
        Captured in the audit log with your reason.
      </p>
      <div className="mt-4">
        <label className="block text-[11px] font-medium uppercase tracking-[0.12em] text-muted">
          Reason
        </label>
        <textarea
          rows={3}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          maxLength={500}
          className="mt-2 w-full rounded-md border border-brown-100 bg-white px-3 py-2 text-sm text-brown-500 placeholder:text-[#8a8a85] focus:border-ink focus:outline-none focus:ring-2 focus:ring-rust/30"
          placeholder="e.g. duplicate signup, spam, employee left, etc."
        />
        <p className="mt-1 text-xs text-brown-300">
          {reason.length} / 500
        </p>
      </div>
      {errorMsg && (
        <p className="mt-4 rounded-md border border-rust-300 bg-rust-50 px-3 py-2 text-sm text-rust-600">
          {errorMsg}
        </p>
      )}
      <div className="mt-6 flex justify-end gap-2">
        <Button variant="ghost" size="sm" onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant="destructive"
          size="sm"
          onClick={() => mutation.mutate()}
          disabled={reason.trim().length === 0 || mutation.isPending}
        >
          {mutation.isPending ? "Rejecting…" : "Reject submission"}
        </Button>
      </div>
    </DialogShell>
  );
}

// ─── New-client dialog ───────────────────────────────────────────────

function NewClientDialog({ onClose }: { onClose: () => void }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const [name, setName] = useState("");
  const [status, setStatus] = useState<"prospect" | "active">("prospect");
  const [notes, setNotes] = useState("");
  // How they found us — required (CEO 2026-10-21, marketing attribution).
  const [lead, setLead] = useState<LeadSourceValue>({ source: "", detail: "" });
  // Asked (and required) only once the database step has run (S1 review F2).
  const leadReady = useLeadSourceReady();
  // Account rep is required (Stephanie 2026-10-14) — tracking and document
  // emails go out as this person. A rep adding a client starts on themselves.
  const [repId, setRepId] = useState<string>(
    user?.roles.includes("client_rep") && user.sub ? user.sub : "",
  );
  const repsQuery = useQuery({
    queryKey: ["clients", "reps"],
    queryFn: () => api<{ reps: Array<{ id: string; name: string }> }>("/clients/reps"),
    staleTime: 5 * 60_000,
  });
  // Initial contact (optional). If firstName + lastName are both filled,
  // they get sent as `initial_contact` on the POST and the backend
  // creates the contact in the same transaction (isPrimary=true).
  const [contactFirstName, setContactFirstName] = useState("");
  const [contactLastName, setContactLastName] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [contactPhone, setContactPhone] = useState("");
  const [contactTitle, setContactTitle] = useState("");

  const hasContactDraft =
    contactFirstName.trim().length > 0 ||
    contactLastName.trim().length > 0 ||
    contactEmail.trim().length > 0 ||
    contactPhone.trim().length > 0 ||
    contactTitle.trim().length > 0;

  const contactReady =
    contactFirstName.trim().length > 0 && contactLastName.trim().length > 0;

  const mutation = useMutation({
    mutationFn: async () => {
      const body: {
        name: string;
        status: string;
        client_rep_id: string;
        notes?: string;
        lead_source?: string;
        lead_source_detail?: string;
        initial_contact?: {
          first_name: string;
          last_name: string;
          email?: string;
          phone?: string;
          title?: string;
        };
      } = {
        name: name.trim(),
        status,
        client_rep_id: repId,
        ...leadSourceBody(lead, leadReady),
      };
      if (notes.trim().length > 0) body.notes = notes.trim();
      if (contactReady) {
        body.initial_contact = {
          first_name: contactFirstName.trim(),
          last_name: contactLastName.trim(),
        };
        if (contactEmail.trim().length > 0) {
          body.initial_contact.email = contactEmail.trim();
        }
        if (contactPhone.trim().length > 0) {
          body.initial_contact.phone = contactPhone.trim();
        }
        if (contactTitle.trim().length > 0) {
          body.initial_contact.title = contactTitle.trim();
        }
      }
      return api<{ client: { id: string }; contact: unknown }>("/clients", {
        method: "POST",
        body: JSON.stringify(body),
      });
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ["clients"] });
      onClose();
      if (result?.client?.id) {
        navigate(`/clients/${result.client.id}`);
      }
    },
  });

  let errorMsg: string | null = null;
  if (mutation.error instanceof ApiError) {
    const body = (mutation.error.body ?? {}) as { error?: string; detail?: string };
    if (body.error === "email_exists") {
      errorMsg =
        body.detail ??
        "A contact with that email already exists. Try without the contact, or use a different email.";
    } else if (body.error === "lead_source_required") {
      errorMsg = body.detail ?? "Pick how this client found us.";
    } else {
      errorMsg = `Couldn't create account (${mutation.error.status})`;
    }
  } else if (mutation.error) {
    errorMsg = "Couldn't create account — see console";
  }

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (name.trim().length === 0 || !repId || !leadSourceComplete(lead, leadReady)) return;
    // If the operator started filling out the contact section but only
    // entered one of first/last name, gently block submit and prompt.
    if (hasContactDraft && !contactReady) return;
    mutation.mutate();
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-brown-500/40 px-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <form
        onSubmit={submit}
        className="w-full max-w-lg max-h-[90vh] overflow-y-auto rounded-lg border border-brown-100 bg-white shadow-xl"
      >
        <div className="flex items-center justify-between border-b border-brown-50 px-5 py-3">
          <h2 className="font-display text-lg font-bold text-brown-500">
            New account
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="text-brown-300 hover:text-brown-500"
            aria-label="Close dialog"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="space-y-4 px-5 py-4">
          <div>
            <label
              htmlFor="new-client-name"
              className="block text-[11px] font-medium uppercase tracking-[0.12em] text-muted"
            >
              Company name
            </label>
            <Input
              id="new-client-name"
              autoFocus
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={200}
              placeholder="e.g. Acme Holdings"
              className="mt-2"
            />
          </div>
          <div>
            <label
              htmlFor="new-client-rep"
              className="block text-[11px] font-medium uppercase tracking-[0.12em] text-muted"
            >
              Account rep
            </label>
            <select
              id="new-client-rep"
              required
              value={repId}
              onChange={(e) => setRepId(e.target.value)}
              className="mt-2 h-10 w-full rounded-md border border-rule bg-white px-3 text-sm text-ink"
            >
              <option value="" disabled>
                {repsQuery.isLoading ? "Loading reps…" : "Pick the account rep"}
              </option>
              {(repsQuery.data?.reps ?? []).map((r) => (
                <option key={r.id} value={r.id}>
                  {r.name}
                </option>
              ))}
            </select>
          </div>
          {leadReady && <LeadSourceField id="new-client-lead-source" value={lead} onChange={setLead} />}
          <div>
            <label className="block text-[11px] font-medium uppercase tracking-[0.12em] text-muted">
              Status
            </label>
            <div className="mt-2 flex gap-2">
              {(["prospect", "active"] as const).map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => setStatus(s)}
                  className={`rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-wide transition-colors ${
                    status === s
                      ? "bg-brown-500 text-cream-50"
                      : "bg-cream-100 text-brown-400 hover:bg-cream-200"
                  }`}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label
              htmlFor="new-client-notes"
              className="block text-[11px] font-medium uppercase tracking-[0.12em] text-muted"
            >
              Notes <span className="font-normal text-brown-300">(optional)</span>
            </label>
            <textarea
              id="new-client-notes"
              rows={3}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              maxLength={2000}
              placeholder="Industry, intro context, anything that helps the next rep…"
              className="mt-2 w-full rounded-md border border-brown-100 bg-white px-3 py-2 text-sm text-brown-500 placeholder:text-[#8a8a85] focus:border-ink focus:outline-none focus:ring-2 focus:ring-rust/30"
            />
          </div>

          {/* Initial contact — optional, but if any field is filled then
              first + last name become required. Backend creates the
              contact in the same transaction as the client (isPrimary). */}
          <div className="rounded-md border border-brown-100 bg-cream-50 p-4">
            <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-brown-400">
              Initial contact
            </div>
            <p className="mb-3 text-xs text-brown-300">
              Optional. Add a first name + last name to create their contact
              alongside the client; rest are optional. Marked primary on
              create.
            </p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label
                  htmlFor="new-contact-first"
                  className="block text-[11px] font-medium uppercase tracking-[0.12em] text-muted"
                >
                  First name
                </label>
                <Input
                  id="new-contact-first"
                  value={contactFirstName}
                  onChange={(e) => setContactFirstName(e.target.value)}
                  maxLength={120}
                  placeholder="Maya"
                  className="mt-2"
                />
              </div>
              <div>
                <label
                  htmlFor="new-contact-last"
                  className="block text-[11px] font-medium uppercase tracking-[0.12em] text-muted"
                >
                  Last name
                </label>
                <Input
                  id="new-contact-last"
                  value={contactLastName}
                  onChange={(e) => setContactLastName(e.target.value)}
                  maxLength={120}
                  placeholder="Smith"
                  className="mt-2"
                />
              </div>
              <div className="col-span-2">
                <label
                  htmlFor="new-contact-email"
                  className="block text-[11px] font-medium uppercase tracking-[0.12em] text-muted"
                >
                  Email
                </label>
                <Input
                  id="new-contact-email"
                  type="email"
                  value={contactEmail}
                  onChange={(e) => setContactEmail(e.target.value)}
                  maxLength={320}
                  placeholder="maya@acme.com"
                  className="mt-2"
                />
              </div>
              <div>
                <label
                  htmlFor="new-contact-phone"
                  className="block text-[11px] font-medium uppercase tracking-[0.12em] text-muted"
                >
                  Phone
                </label>
                <Input
                  id="new-contact-phone"
                  type="tel"
                  value={contactPhone}
                  onChange={(e) => setContactPhone(e.target.value)}
                  maxLength={60}
                  placeholder="+1 555 555 5555"
                  className="mt-2"
                />
              </div>
              <div>
                <label
                  htmlFor="new-contact-title"
                  className="block text-[11px] font-medium uppercase tracking-[0.12em] text-muted"
                >
                  Title
                </label>
                <Input
                  id="new-contact-title"
                  value={contactTitle}
                  onChange={(e) => setContactTitle(e.target.value)}
                  maxLength={160}
                  placeholder="VP of Marketing"
                  className="mt-2"
                />
              </div>
            </div>
            {hasContactDraft && !contactReady && (
              <p className="mt-3 text-xs text-rust-600">
                First name and last name are both required to add this
                contact. Clear all fields if you don't want a contact.
              </p>
            )}
          </div>

          {errorMsg && (
            <p className="rounded-md border border-rust-300 bg-rust-50 px-3 py-2 text-sm text-rust-600">
              {errorMsg}
            </p>
          )}
        </div>
        <div className="flex justify-end gap-2 border-t border-brown-50 px-5 py-3">
          <Button type="button" variant="ghost" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            size="sm"
            disabled={
              name.trim().length === 0 ||
              !repId ||
              !leadSourceComplete(lead, leadReady) ||
              mutation.isPending ||
              (hasContactDraft && !contactReady)
            }
          >
            {mutation.isPending ? "Creating…" : "Create account"}
          </Button>
        </div>
      </form>
    </div>
  );
}

// ─── Empty states ────────────────────────────────────────────────────

interface EmptyStateProps {
  empty: boolean;
  isAdmin: boolean;
  onSeed: () => void;
  seeding: boolean;
}

function EmptyState({
  empty,
  isAdmin,
  onSeed,
  seeding,
}: EmptyStateProps) {
  if (!empty) {
    return (
      <div className="px-5 py-12 text-center">
        <p className="font-medium text-ink">No accounts match.</p>
        <p className="mt-1 text-sm text-muted">Clear the search or choose All.</p>
      </div>
    );
  }
  return (
    <div className="px-5 py-14 text-center">
      <p className="font-medium text-ink">No accounts yet.</p>
      <p className="mx-auto mt-1 max-w-md text-sm text-muted">
        Add the first company you sell to with New account.
        {isAdmin ? " Or seed sample data to try things out." : ""}
      </p>
      {isAdmin && (
        <Button
          variant="outline"
          className="mt-4"
          onClick={onSeed}
          disabled={seeding}
        >
          <Sparkles className="h-4 w-4" />
          {seeding ? "Seeding…" : "Seed 30 sample accounts"}
        </Button>
      )}
    </div>
  );
}

function BacklogEmpty() {
  return (
    <div className="px-5 py-14 text-center">
      <p className="font-medium text-ink">Nothing to review.</p>
      <p className="mx-auto mt-1 max-w-md text-sm text-muted">
        No self-registered companies pending review. New portal sign-ups
        land here for an Account Manager to triage.
      </p>
    </div>
  );
}
